/**
 * Reusable HTTP tool definitions — the DB-backed half of the tool story.
 *
 * WHAT A ROW HERE IS: a tool defined by an operator — a name, a JSON Schema,
 * and a URL of the customer's own that the Durable Object POSTs to when the
 * model calls it. THIS SCREEN IS THE LIST AND THE CREATE FORM ONLY; a row
 * click opens /tools/:id (ToolEdit.tsx), which owns editing, testing and
 * revoking. Attaching a tool to an agent happens on the agent form.
 *
 * NAMES: renamable while live, burned on revoke. Transcripts and `tool_calls`
 * record calls by the name that was current AT CALL TIME, so a rename (done on
 * the tool's own page) changes what new sessions call the tool without
 * rewriting history — and a REVOKED tool's name is never reused, which is why
 * revoking warns that the name is burned.
 *
 * DISPLAY NAME AND DESCRIPTION are optional operator-written strings for END
 * USERS: the voice widget's chat panel shows them on tool cards instead of the
 * raw `rtx_search`. Informational only — the model keeps reading `name` and
 * `description`, and nothing about the provider config changes.
 *
 * HEADER VALUES ARE WRITE-ONLY — see components/headers-editor.tsx for the full
 * argument. This page's contribution is the consequence: reads show names only,
 * and the edit form must be honest that an edit re-sends the whole header set.
 *
 * THE PARAMS TEXTAREA IS CHECKED CLIENT-SIDE BEFORE THE POST — `JSON.parse`
 * plus "is it a JSON object" — because a schema is typed by hand here and a
 * comma-in-the-wrong-place should surface while the reader is still looking at
 * the line, not as a server 400 quoting the whole document back. The server's
 * own check (object-TYPE schema, ≤8000 bytes) remains the authority; anything
 * it refuses that this copy missed is shown verbatim.
 *
 * The mechanics — inline create in a panel, epoch-counter refetch after every
 * write, two-click revoke, `.err` lines, no toasts and no modals — are
 * Users.tsx's, and deviations from that template would be bugs.
 */
import { useEffect, useState } from 'react'
import { toolDefs, type CreateToolDefBody, type ToolDefView } from '@/api'
import { can } from '@/lib/auth'
import { hostOf, parseParams, toolMessageFor as messageFor } from '@/lib/tool-form'
import { useAuth } from '@/components/auth-provider'
import { navigate } from '@/components/app-sidebar'
import { DataTable, PageHeader, type Column } from '@/components/data-table'
import { buildHeaders, headerRowsClean, HeadersEditor, type HeaderRow } from '@/components/headers-editor'
import { fmtWhen } from '@/components/state-pill'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

/**
 * A starter schema rather than an empty box: the server requires an object-type
 * schema, and the fastest way to teach the shape is to show it. `properties`
 * left empty on purpose — a tool with no arguments is legitimate.
 */
const PARAMS_SEED = '{\n  "type": "object",\n  "properties": {}\n}'

export function ToolsPage() {
  const { user } = useAuth()
  const canWrite = can(user, 'write')

  const [rows, setRows] = useState<ToolDefView[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  // Bumped after every successful write. A counter rather than a boolean because
  // two consecutive writes must be two distinct dependency values.
  const [epoch, setEpoch] = useState(0)

  /** The create form starts CLOSED: the table is this page's face, and a
   *  permanently-open form above it once hid the edit panel well enough that
   *  "there is no way to edit" was a reasonable reading. */
  const [showCreate, setShowCreate] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    toolDefs
      .list(ac.signal)
      .then((res) => {
        if (ac.signal.aborted) return
        setRows(res.rows)
        setLoadError(null)
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        setLoadError(messageFor(e))
      })
    return () => ac.abort()
  }, [epoch])

  // ------------------------------------------------------------------ create
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [displayDescription, setDisplayDescription] = useState('')
  const [url, setUrl] = useState('')
  const [includeTranscript, setIncludeTranscript] = useState(false)
  // On by default — matching the server default and the pre-flag behaviour.
  const [sendClientPayload, setSendClientPayload] = useState(true)
  const [paramsText, setParamsText] = useState(PARAMS_SEED)
  const [headerRows, setHeaderRows] = useState<HeaderRow[]>([])
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  const onCreate = (e: React.FormEvent) => {
    e.preventDefault()
    if (creating) return
    // The client-side JSON check. Rejected HERE, before the POST — see the
    // file header. The header rows get the same treatment via headerRowsClean.
    const params = parseParams(paramsText)
    if ('err' in params) {
      setCreateError(params.err)
      return
    }
    if (!headerRowsClean(headerRows, true)) {
      setCreateError('Fix the header rows first — each needs a valid name and a value.')
      return
    }
    const body: CreateToolDefBody = {
      name: name.trim(),
      description: description.trim(),
      url: url.trim(),
      parameters: params.ok,
      includeTranscript,
      sendClientPayload,
    }
    if (displayName.trim() !== '') body.displayName = displayName.trim()
    if (displayDescription.trim() !== '') body.displayDescription = displayDescription.trim()
    if (headerRows.length > 0) body.headers = buildHeaders(headerRows)
    setCreating(true)
    setCreateError(null)
    toolDefs
      .create(body, AbortSignal.timeout(20_000))
      .then((row) => {
        // Straight to the new tool's own page — the next step (attach it, test
        // it) lives there, and the list would only make the reader find it.
        navigate(`/tools/${row.id}`)
      })
      .catch((err: unknown) => {
        setCreating(false)
        setCreateError(messageFor(err))
      })
  }

  // ------------------------------------------------------------------- table
  const columns: Column<ToolDefView>[] = [
    {
      key: 'name',
      header: 'Name',
      width: '14rem',
      cell: (t) => (
        <span className="flex min-w-0 flex-col">
          <span className="flex items-center gap-1.5">
            <span className="mono truncate font-medium">{t.name}</span>
            {!t.enabled && (
              <span className="pill pill-plain shrink-0" title="Inactive: agents keep the reference, new sessions skip it. Flip it on the tool's page.">
                inactive
              </span>
            )}
          </span>
          {t.displayName && (
            <span className="truncate text-xs text-muted-foreground" title="Display name — what end users see on the widget's tool card.">
              {t.displayName}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      // One truncated line under `fixed` layout: the row is a link to the
      // detail page, which is where the full text lives.
      cell: (t) => (
        <span className="block truncate text-sm" title={t.description}>
          {t.description}
        </span>
      ),
    },
    {
      key: 'url',
      header: 'Endpoint',
      width: '13rem',
      cell: (t) => (
        // Host only in the table; the full URL is on the tool's page. A URL's
        // path or query can carry a credential, and this column is on screen
        // during every demo.
        <span className="mono block truncate text-xs" title="Host only here — open the tool for the full URL.">
          {hostOf(t.url)}
        </span>
      ),
    },
    {
      key: 'transcript',
      header: 'Transcript',
      width: '7rem',
      hideBelow: 'md',
      cell: (t) =>
        t.includeTranscript ? (
          <span
            className="pill border-ev-warn-border bg-ev-warn-bg text-ev-warn"
            title="Each call POSTs the transcript so far (capped) to the endpoint, along with the arguments."
          >
            sent
          </span>
        ) : (
          <span className="pill pill-plain" title="Only the model's arguments are sent.">
            not sent
          </span>
        ),
    },
    {
      key: 'clientPayload',
      header: 'Client params',
      width: '7rem',
      hideBelow: 'md',
      cell: (t) =>
        t.sendClientPayload ? (
          <span
            className="pill pill-plain"
            title="Each call POSTs the session's client data params alongside the arguments."
          >
            sent
          </span>
        ) : (
          <span className="pill pill-plain" title="The POST body carries no params key.">
            not sent
          </span>
        ),
    },
    {
      key: 'headers',
      header: 'Headers',
      width: '6rem',
      hideBelow: 'md',
      cell: (t) => (
        <span
          className="text-sm"
          title={
            t.headerNames.length === 0
              ? 'No custom headers.'
              : `Names only — values are write-only: ${t.headerNames.join(', ')}`
          }
        >
          {t.headerNames.length}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      width: '11rem',
      hideBelow: 'lg',
      cell: (t) => <span className="whitespace-nowrap">{fmtWhen(t.createdAt)}</span>,
    },
  ]


  return (
    <div className="min-w-0">
      <PageHeader
        title="Tools"
        subtitle={
          <>
            A tool is a name, a JSON Schema, and a URL of your own that the session&rsquo;s
            Durable Object POSTs to when the model calls it. Attach one to an agent on the
            agent&rsquo;s own page; Test fires it once from here, no agent needed.
          </>
        }
        actions={
          canWrite ? (
            <Button size="sm" className="h-8" onClick={() => setShowCreate((v) => !v)}>
              {showCreate ? 'Close the form' : 'New tool'}
            </Button>
          ) : undefined
        }
      />

      {canWrite && showCreate ? (
        <section className="panel">
          <h2>
            Define a tool
            <span className="note">calls are recorded under the name current at call time</span>
          </h2>
          <form onSubmit={onCreate} className="body flex flex-col gap-4">
            <div className="grid2">
              <div className="field">
                <Label htmlFor="toolName">Name</Label>
                <Input
                  id="toolName"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mono"
                  placeholder="check_order"
                  autoCapitalize="none"
                  spellCheck={false}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Lower-case letters, digits, <span className="mono">_</span> and{' '}
                  <span className="mono">-</span>, up to 64 characters. This is the name the
                  model calls. It can be renamed later on the tool&rsquo;s page; a revoked
                  tool&rsquo;s name is never reused. Must not collide with a built-in tool.
                </p>
              </div>
              <div className="field">
                <Label htmlFor="toolUrl">Endpoint URL</Label>
                <Input
                  id="toolUrl"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="mono"
                  placeholder="https://api.example.com/voice/tools"
                  autoCapitalize="none"
                  spellCheck={false}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  HTTPS, reachable from the public internet. Private addresses and credentials
                  in the URL are refused at save time and re-checked at fire time.
                </p>
              </div>
            </div>

            <div className="field">
              <Label htmlFor="toolDescription">Description</Label>
              <Textarea
                id="toolDescription"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                required
              />
              <p className="text-xs text-muted-foreground">
                For the model, not for this page: it decides when to call the tool based on
                this text, so write it as an instruction about when the tool applies.
              </p>
            </div>

            <div className="grid2">
              <div className="field">
                <Label htmlFor="toolDisplayName">Display name</Label>
                <Input
                  id="toolDisplayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Order lookup"
                  maxLength={120}
                />
                <p className="text-xs text-muted-foreground">
                  Optional. What end users see on the widget&rsquo;s tool card instead of the
                  raw name. Purely informational — the model never reads it.
                </p>
              </div>
              <div className="field">
                <Label htmlFor="toolDisplayDescription">Display description</Label>
                <Input
                  id="toolDisplayDescription"
                  value={displayDescription}
                  onChange={(e) => setDisplayDescription(e.target.value)}
                  placeholder="Checks an order's status"
                  maxLength={300}
                />
                <p className="text-xs text-muted-foreground">
                  Optional. A short line under the display name, same audience: end users,
                  never the model.
                </p>
              </div>
            </div>

            <div className="field">
              <Label htmlFor="toolParams">Parameters — JSON Schema</Label>
              <Textarea
                id="toolParams"
                value={paramsText}
                onChange={(e) => setParamsText(e.target.value)}
                rows={8}
                className="font-mono text-xs"
                spellCheck={false}
              />
              <p className="text-xs text-muted-foreground">
                Must be a JSON object with <span className="mono">&quot;type&quot;: &quot;object&quot;</span>,
                8000 bytes at most. Checked here before anything is sent, so a stray comma
                shows up while the line is still on screen.
              </p>
            </div>

            <HeadersEditor idPrefix="toolCreate" rows={headerRows} onChange={setHeaderRows} />

            <div className="field">
              <label
                htmlFor="toolTranscript"
                className="flex cursor-pointer items-center gap-2 text-sm font-medium"
              >
                <Checkbox
                  id="toolTranscript"
                  checked={includeTranscript}
                  onCheckedChange={(v) => setIncludeTranscript(v === true)}
                />
                Send the transcript with each call
              </label>
              <p className="text-xs text-muted-foreground">
                POSTs the conversation so far (capped) alongside the arguments. Only for
                endpoints that genuinely need context — it is the whole conversation, sent to
                the URL above on every call.
              </p>
            </div>

            <div className="field">
              <label
                htmlFor="toolClientPayload"
                className="flex cursor-pointer items-center gap-2 text-sm font-medium"
              >
                <Checkbox
                  id="toolClientPayload"
                  checked={sendClientPayload}
                  onCheckedChange={(v) => setSendClientPayload(v === true)}
                />
                Send the client payload with each call
              </label>
              <p className="text-xs text-muted-foreground">
                POSTs the session&rsquo;s client data params (<span className="mono">params</span>)
                alongside the arguments — everything the embedding page or the init webhook
                put on the session. Untick it for endpoints that have no business seeing
                client data; the body then carries no <span className="mono">params</span> key.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" disabled={creating || name.trim() === '' || url.trim() === ''}>
                {creating ? 'Creating…' : 'Create tool'}
              </Button>
              {createError && <p className="err text-sm">{createError}</p>}
            </div>
          </form>
        </section>
      ) : null}
      {!canWrite && (
        <p className="mb-4 text-sm text-muted-foreground">
          Read-only: defining and editing tools is a write, and this account is a{' '}
          <span className="mono">{user?.role}</span>.
        </p>
      )}



      {loadError && <p className="err mb-4">{loadError}</p>}

      <DataTable
        columns={columns}
        rows={rows}
        keyOf={(t) => t.id}
        // Fixed layout so no cell can force horizontal scroll: description and
        // endpoint truncate with an ellipsis; a row click opens the tool's own
        // page, which holds the full values, the editor and the test panel.
        fixed
        onRowClick={(t) => navigate(`/tools/${t.id}`)}
        emptyTitle="No tools yet"
        emptyBody={
          <>
            Nothing is defined yet. Press <strong>New tool</strong> above to define the
            first one — a name, a schema, and an endpoint of yours to POST to.
          </>
        }
      />
    </div>
  )
}
